import React, { useState, useEffect, useRef } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as productionActions from '../../actions/production';
import { withRouter } from 'react-router';
import { toast } from 'react-toastify';
import { axiosCaller } from '../../utils/apiCaller';
import './WorkerDashboard.scss';

interface Props {
    history: any;
    actions: any;
    productionActions: any;
    permissions: any;
    user: any;
    production: any;
}

const STATUS_COMPLETED = 1;
const STATUS_WAITING = 2;
const STATUS_RETURNED = 3;
const STATUS_IN_PROCESS = 4;
const STATUS_PAUSE = 5;
const STATUS_END_SHIFT = 6;
const STATUS_OTHER_JOB = 7;
const STATUS_OTHER_REASON = 8;

const statusLabels: Record<number, string> = {
    [STATUS_COMPLETED]: 'Përfunduar',
    [STATUS_WAITING]: 'Në pritje',
    [STATUS_RETURNED]: 'Kthyer',
    [STATUS_IN_PROCESS]: 'Në proces',
    [STATUS_PAUSE]: 'Pauzë',
    [STATUS_END_SHIFT]: 'Mbarimi i orarit',
    [STATUS_OTHER_JOB]: 'Punë tjetër',
    [STATUS_OTHER_REASON]: 'Arsye tjetër',
};

// Process colors for left border — consistent per process type
const processColors = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#6366f1', '#14b8a6', '#f97316'];
const getProcessColor = (responsibilityId: number) => processColors[responsibilityId % processColors.length];

// Albanian day/month names
const dayNames = ['E diel', 'E hënë', 'E martë', 'E mërkurë', 'E enjte', 'E premte', 'E shtunë'];
const monthNames = ['Janar', 'Shkurt', 'Mars', 'Prill', 'Maj', 'Qershor', 'Korrik', 'Gusht', 'Shtator', 'Tetor', 'Nëntor', 'Dhjetor'];

const getGreeting = (): string => {
    const h = new Date().getHours();
    if (h < 12) return 'Mirëmëngjes';
    if (h < 18) return 'Mirëdita';
    return 'Mirëmbrëma';
};

const getTodayDate = (): string => {
    const d = new Date();
    return `${dayNames[d.getDay()]}, ${d.getDate()} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
};

const WorkerDashboard: React.FC<Props> = (props) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [showDoneToday, setShowDoneToday] = useState(false);
    const [showPaused, setShowPaused] = useState(true);
    const [blueprintFiles, setBlueprintFiles] = useState<Record<number, any[]>>({});
    const [showPauseFor, setShowPauseFor] = useState<string | null>(null);
    const [activeMaterialKey, setActiveMaterialKey] = useState<string | null>(null);
    const [blockModal, setBlockModal] = useState<{ item: any; material: any | null } | null>(null);
    const [blockComment, setBlockComment] = useState('');
    const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() =>
        (localStorage.getItem('wdViewMode') as 'list' | 'kanban') || 'kanban'
    );
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [processFilter, setProcessFilter] = useState<number | null>(null);
    const [moreMenuKey, setMoreMenuKey] = useState<string | null>(null);
    const timerRef = useRef<any>(null);
    const [tick, setTick] = useState(0);

    const userName = localStorage.getItem('username') || '';

    const updatingRef = useRef(false);

    // Timer tick every second for live timers
    useEffect(() => {
        timerRef.current = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(timerRef.current);
    }, []);

    // Close more menu on outside click
    useEffect(() => {
        if (!moreMenuKey) return;
        const handler = () => setMoreMenuKey(null);
        setTimeout(() => document.addEventListener('click', handler), 0);
        return () => document.removeEventListener('click', handler);
    }, [moreMenuKey]);

    // Initial load + polling every 10 seconds
    useEffect(() => {
        loadTasks();
        const pollInterval = setInterval(() => {
            if (!updatingRef.current) {
                axiosCaller('GET', 'production/my-tasks').then((res: any) => {
                    if (res?.data) setData(res.data);
                }).catch(() => {});
            }
        }, 10000);
        return () => clearInterval(pollInterval);
    }, []);

    const loadTasks = () => {
        axiosCaller('GET', 'production/my-tasks').then((res: any) => {
            setData(res?.data);
            setLoading(false);
        }).catch(() => { setLoading(false); toast.error('Nuk u ngarkuan të dhënat'); });
    };

    const loadBlueprints = (productId: number) => {
        if (blueprintFiles[productId]) return;
        axiosCaller('GET', `project-blueprint/${productId}/products`).then((res: any) => {
            const files = res?.data?.data || [];
            setBlueprintFiles(prev => ({ ...prev, [productId]: files }));
        }).catch(() => {});
    };

    // ── Filter helpers ──
    const filterByProcess = (items: any[]) => {
        if (!processFilter) return items;
        return items.filter((item: any) => item.responsibilityId === processFilter);
    };

    const getFilteredData = () => {
        if (!data) return { inProgress: [], blocked: [], waiting: [], paused: [], completedToday: [] };
        return {
            inProgress: filterByProcess(data.inProgress || []),
            blocked: filterByProcess(data.blocked || []),
            waiting: filterByProcess(data.waiting || []),
            paused: filterByProcess(data.paused || []),
            completedToday: filterByProcess(data.completedToday || []),
        };
    };

    const toggleStatusFilter = (key: string) => {
        setStatusFilter(statusFilter === key ? null : key);
    };

    const toggleProcessFilter = (id: number) => {
        setProcessFilter(processFilter === id ? null : id);
    };

    const getLiveTimer = (tracker: any): string => {
        if (!tracker) return '00:00:00';
        let total = tracker.duration || 0;
        if (tracker.isRunning && tracker.currentSessionStart) {
            const elapsed = Math.floor((Date.now() - new Date(tracker.currentSessionStart).getTime()) / 1000);
            total += elapsed;
        }
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    const isOverdue = (dateStr: string | null): boolean => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    };

    const updateMaterialStatus = (item: any, material: any, newStatusId: number, warning?: string) => {
        setUpdatingId(material.materialStatusId);
        updatingRef.current = true;
        const payload: any = {
            productId: item.productId,
            responsibilityStatusId: newStatusId,
            responsibilityId: item.responsibilityId,
            materialId: material.isTemporary ? null : material.materialId,
            temporaryMaterialId: material.isTemporary ? material.temporaryMaterialId : null,
        };
        if (warning) payload.warning = warning;
        axiosCaller('POST', 'responsibilities/clients/material/statuses', payload).then(() => {
            toast.success('Statusi u përditësua!');
            setUpdatingId(null);
            updatingRef.current = false;
            setActiveMaterialKey(null);
            setMoreMenuKey(null);
            setBlockComment('');
            loadTasks();
        }).catch(() => { toast.error('Diqka nuk shkoi mirë'); setUpdatingId(null); updatingRef.current = false; });
    };

    const updateProcessStatus = (item: any, newStatusId: number, warning?: string) => {
        setUpdatingId(item.productStatusId);
        updatingRef.current = true;
        const payload: any = {
            productId: item.productId,
            responsibilityStatusId: newStatusId,
            responsibilityId: item.responsibilityId,
        };
        if (warning) payload.warning = warning;
        axiosCaller('POST', 'responsibilities/clients/statuses', payload).then(() => {
            toast.success('Statusi u përditësua!');
            setUpdatingId(null);
            updatingRef.current = false;
            setActiveMaterialKey(null);
            setMoreMenuKey(null);
            setBlockComment('');
            loadTasks();
        }).catch(() => { toast.error('Diqka nuk shkoi mirë'); setUpdatingId(null); updatingRef.current = false; });
    };

    const getItemKey = (item: any) => `${item.productId}-${item.responsibilityId}`;

    const toggleExpand = (item: any) => {
        const key = getItemKey(item);
        if (expandedItem === key) {
            setExpandedItem(null);
        } else {
            setExpandedItem(key);
            loadBlueprints(item.productId);
        }
    };

    const handleViewChange = (mode: 'list' | 'kanban') => {
        setViewMode(mode);
        localStorage.setItem('wdViewMode', mode);
    };

    // ── Action Buttons ──
    const renderActions = (item: any, material: any | null, statusId: number) => {
        const isProcess = !material;
        const update = (s: number, w?: string) => {
            if (isProcess) updateProcessStatus(item, s, w);
            else updateMaterialStatus(item, material, s, w);
            setActiveMaterialKey(null);
            setMoreMenuKey(null);
        };
        const busy = updatingId === (material ? material.materialStatusId : item.productStatusId);
        const moreKey = `${item.productId}-${item.responsibilityId}-${material?.materialStatusId || 'p'}`;
        const isMoreOpen = moreMenuKey === moreKey;

        if (statusId === STATUS_COMPLETED) return null;

        return (
            <div className="wd-actions">
                {statusId === STATUS_WAITING && (
                    <button className="wd-btn wd-btn-start" onClick={() => update(STATUS_IN_PROCESS)} disabled={busy}>▶ Fillo</button>
                )}

                {statusId === STATUS_IN_PROCESS && (
                    <div className="wd-actions-row" style={{ position: 'relative' }}>
                        <button className="wd-btn wd-btn-complete" onClick={() => update(STATUS_COMPLETED)} disabled={busy}>✓ Përfundo</button>
                        <button className={`wd-btn wd-btn-sm wd-btn-more ${isMoreOpen ? 'wd-btn-more-active' : ''}`} onClick={(e) => { e.stopPropagation(); setMoreMenuKey(isMoreOpen ? null : moreKey); }}>⋮ Më shumë</button>
                        {isMoreOpen && (
                            <div className="wd-more-menu">
                                <button className="wd-more-item" onClick={() => update(STATUS_PAUSE)} disabled={busy}>⏸ Pauzë</button>
                                <button className="wd-more-item" onClick={() => update(STATUS_END_SHIFT)} disabled={busy}>🕐 Mbarimi i orarit</button>
                                <button className="wd-more-item" onClick={() => update(STATUS_OTHER_JOB)} disabled={busy}>🔄 Punë tjetër</button>
                                <button className="wd-more-item" onClick={() => update(STATUS_OTHER_REASON)} disabled={busy}>❓ Arsye tjetër</button>
                                <div className="wd-more-divider" />
                                <button className="wd-more-item wd-more-block" onClick={() => { setBlockModal({ item, material }); setBlockComment(''); setMoreMenuKey(null); }}>⚠ Raporto bllokim</button>
                            </div>
                        )}
                    </div>
                )}

                {[STATUS_PAUSE, STATUS_END_SHIFT, STATUS_OTHER_JOB, STATUS_OTHER_REASON].includes(statusId) && (
                    <button className="wd-btn wd-btn-start" onClick={() => update(STATUS_IN_PROCESS)} disabled={busy}>▶ Vazhdo</button>
                )}

                {statusId === STATUS_RETURNED && (
                    <button className="wd-btn wd-btn-start" onClick={() => update(STATUS_IN_PROCESS)} disabled={busy}>▶ Fillo përsëri</button>
                )}
            </div>
        );
    };

    // ── Material Row — tap to select, only selected shows actions ──
    const renderMaterial = (item: any, material: any) => {
        const isActive = material.statusId === STATUS_IN_PROCESS;
        const isLocked = material.isAvailable === false && material.statusId === STATUS_WAITING;
        const isDone = material.statusId === STATUS_COMPLETED;
        const matKey = `${item.productId}-${item.responsibilityId}-${material.materialStatusId}`;
        const isSelected = activeMaterialKey === matKey;
        const canAct = !isLocked && !isDone;

        return (
            <div key={material.materialStatusId} className={`wd-material ${isActive ? 'wd-material-active' : ''} ${isLocked ? 'wd-material-locked' : ''} ${isDone ? 'wd-material-done' : ''} ${isSelected ? 'wd-material-selected' : ''}`}>
                <div className="wd-material-row" onClick={canAct ? () => setActiveMaterialKey(isSelected ? null : matKey) : undefined} style={canAct ? { cursor: 'pointer' } : {}}>
                    <div className="wd-material-info">
                        <span className={`wd-material-dot ${isActive ? 'wd-dot-active' : isDone ? 'wd-dot-done' : isLocked ? 'wd-dot-locked' : 'wd-dot-waiting'}`} />
                        <span className="wd-material-name">{material.materialName || 'Pa emër'}</span>
                    </div>
                    <div className="wd-material-right">
                        {isActive && material.tracker?.isRunning && (
                            <span className="wd-material-timer">{getLiveTimer(material.tracker)}</span>
                        )}
                        {isDone && <span className="wd-material-badge-done">✓</span>}
                        {isLocked && <span className="wd-material-badge-locked">Duke pritur</span>}
                        {canAct && !isDone && <span className={`wd-material-chevron ${isSelected ? 'wd-chevron-open' : ''}`}></span>}
                    </div>
                </div>
                {isSelected && renderActions(item, material, material.statusId)}
            </div>
        );
    };

    // ── Blueprints ──
    const renderBlueprints = (productId: number) => {
        const files = blueprintFiles[productId] || [];
        if (!files || files.length === 0) return null;

        return (
            <div className="wd-blueprints">
                <div className="wd-blueprints-label">📐 Skicat / Dosjet</div>
                <div className="wd-blueprints-grid">
                    {files.map((file: any, i: number) => (
                        <a key={i} href={file.blueprint} target="_blank" rel="noopener noreferrer" className="wd-blueprint-item">
                            {file.blueprint?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                <img src={file.blueprint} alt="" className="wd-blueprint-thumb" />
                            ) : (
                                <div className="wd-blueprint-file">
                                    <span className="wd-blueprint-ext">{file.blueprint?.split('.').pop()?.toUpperCase() || 'FILE'}</span>
                                </div>
                            )}
                            <span className="wd-blueprint-name">{file.nameOfBlueprint || ''}</span>
                        </a>
                    ))}
                </div>
            </div>
        );
    };

    // ── Card Component ──
    // ── Format time from seconds ──
    const formatDuration = (seconds: number): string => {
        if (!seconds || seconds <= 0) return '';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };

    const formatTime = (dateStr: string): string => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const renderCard = (item: any, section: 'active' | 'blocked' | 'waiting' | 'paused' | 'done') => {
        const key = getItemKey(item);
        const isExpanded = expandedItem === key;
        const pColor = getProcessColor(item.responsibilityId);
        const overdue = isOverdue(item.productEnd);
        const canExpand = true;

        return (
            <div key={key} className={`wd-card wd-card-${section}`}>
                {/* Card face — compact: process + client + product */}
                <div className="wd-card-top" onClick={() => canExpand ? toggleExpand(item) : null}>
                    <div className="wd-card-main">
                        <div className="wd-card-process-row">
                            <span className="wd-card-process" style={{ background: pColor }}>{item.responsibilityName}</span>
                            <div className="wd-card-row-right">
                                {section === 'active' && item.tracker?.isRunning && (
                                    <div className="wd-timer">
                                        <span className="wd-timer-dot" />
                                        <span className="wd-timer-value">{getLiveTimer(item.tracker)}</span>
                                    </div>
                                )}
                                {(section === 'waiting' || section === 'active') && item.productEnd && (
                                    <span className={`wd-card-deadline ${overdue ? 'wd-overdue' : ''}`}>{formatDate(item.productEnd)}</span>
                                )}
                                {section === 'done' && item.completedAt && (
                                    <span className="wd-card-completed-time">{formatTime(item.completedAt)}{item.durationSeconds > 0 ? ` · ${formatDuration(item.durationSeconds)}` : ''}</span>
                                )}
                                {canExpand && (
                                    <span className={`wd-chevron ${isExpanded ? 'wd-chevron-open' : ''}`}>▾</span>
                                )}
                            </div>
                        </div>
                        <div className="wd-card-compact">
                            <span className="wd-card-client">{item.clientName}</span>
                            <span className="wd-card-sep">·</span>
                            <span className="wd-card-product">{item.articleName}{item.productType ? ` (${item.productType})` : ''}</span>
                        </div>
                    </div>
                </div>

                {/* Block/return warning — check process level + material level */}
                {section === 'blocked' && (() => {
                    const processWarning = item.warning;
                    const adminWarning = item.adminWarning;
                    const materialWarnings = (item.materials || []).filter((m: any) => m.warning || m.adminWarning);
                    const hasAny = processWarning || adminWarning || materialWarnings.length > 0;
                    if (!hasAny) return null;
                    return (
                        <div className="wd-block-warning">
                            <span className="wd-block-warn-icon">⚠</span>
                            <div>
                                {adminWarning && (
                                    <><div className="wd-block-warn-label">Kthyer nga admini</div>
                                    <div className="wd-block-warn-text">{adminWarning}</div></>
                                )}
                                {processWarning && (
                                    <><div className="wd-block-warn-label">{adminWarning ? 'Komenti i vonesës' : 'Arsyeja e bllokimit'}</div>
                                    <div className="wd-block-warn-text">{processWarning}</div></>
                                )}
                                {!processWarning && !adminWarning && materialWarnings.map((m: any, i: number) => (
                                    <div key={i}>
                                        <div className="wd-block-warn-label">Bllokimi: {m.materialName}</div>
                                        <div className="wd-block-warn-text">{m.warning || m.adminWarning}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* Expanded details — project, quantity, blueprints */}
                {isExpanded && (
                    <div className="wd-card-expanded">
                        <div className="wd-card-details">
                            {item.projectName && <div className="wd-detail"><span className="wd-label">Projekti</span><span className="wd-value">{item.projectName}</span></div>}
                            {item.quantity && <div className="wd-detail"><span className="wd-label">Sasia</span><span className="wd-value">{parseFloat(item.quantity).toFixed(2)}</span></div>}
                            {item.productStart && <div className="wd-detail"><span className="wd-label">Fillimi</span><span className="wd-value">{formatDate(item.productStart)}</span></div>}
                            {item.productEnd && <div className="wd-detail"><span className="wd-label">Afati</span><span className={`wd-value ${overdue ? 'wd-overdue' : ''}`}>{formatDate(item.productEnd)}</span></div>}
                        </div>
                        {renderBlueprints(item.productId)}
                    </div>
                )}

                {/* Materials — always show for active/returned/paused, show on expand for waiting */}
                {(section !== 'waiting' && section !== 'done' || isExpanded) && (
                    <>
                        {item.hasMaterials ? (
                            <div className="wd-materials">
                                <div className="wd-materials-label">Materialet ({item.materials.length})</div>
                                {item.materials.map((mat: any) => renderMaterial(item, mat))}
                            </div>
                        ) : (
                            section !== 'done' && renderActions(item, null, item.statusId)
                        )}

                        {/* Start button for waiting items without materials */}
                        {section === 'waiting' && !item.hasMaterials && (
                            <div className="wd-expand-action">
                                <button className="wd-btn wd-btn-start wd-btn-full" onClick={() => updateProcessStatus(item, STATUS_IN_PROCESS)} disabled={updatingId === item.productStatusId}>
                                    <span className="wd-btn-icon">▶</span> Fillo punën
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // ── Section Component ──
    const renderSection = (title: string, icon: string, items: any[], section: 'active' | 'blocked' | 'waiting' | 'paused' | 'done', collapsible = false, defaultOpen = true) => {
        if (items.length === 0) return null;
        const [isOpen, setIsOpen] = section === 'done'
            ? [showDoneToday, setShowDoneToday]
            : section === 'paused'
                ? [showPaused, setShowPaused]
                : [true, () => {}];

        return (
            <div className="wd-section">
                <div className={`wd-section-header wd-section-${section}`} onClick={collapsible ? () => setIsOpen(!isOpen) : undefined}>
                    <div className="wd-section-left">
                        <span className="wd-section-icon">{icon}</span>
                        <span className="wd-section-title">{title}</span>
                    </div>
                    <div className="wd-section-right">
                        <span className="wd-section-count">{items.length}</span>
                        {collapsible && <span className={`wd-chevron ${isOpen ? 'wd-chevron-open' : ''}`}>▾</span>}
                    </div>
                </div>
                {(!collapsible || isOpen) && (
                    <div className="wd-section-cards">
                        {items.map(item => renderCard(item, section))}
                    </div>
                )}
            </div>
        );
    };

    // ── Mobile tab state ──
    const [mobileTab, setMobileTab] = useState('active');

    // ── Kanban columns definition ──
    const getColumns = () => {
        const fd = getFilteredData();
        const emptyMessages: Record<string, string> = {
            waiting: 'Nuk keni detyra në pritje',
            active: 'Nuk keni detyra në proces',
            blocked: 'Nuk keni detyra me bllokim',
            paused: 'Nuk keni detyra të pauzuara',
            done: 'Nuk keni detyra të përfunduara',
        };
        return {
            columns: [
                { key: 'waiting', title: 'Në pritje', icon: '⏳', items: fd.waiting, color: '#3b82f6', empty: emptyMessages.waiting },
                { key: 'active', title: 'Në proces', icon: '🔄', items: fd.inProgress, color: '#eab308', empty: emptyMessages.active },
                ...(fd.blocked.length > 0 ? [{ key: 'blocked', title: 'Të bllokuara', icon: '⚠', items: fd.blocked, color: '#dc2626', empty: emptyMessages.blocked }] : []),
                { key: 'paused', title: 'Pauzuara', icon: '⏸', items: fd.paused, color: '#f97316', empty: emptyMessages.paused },
                { key: 'done', title: 'Sot', icon: '✅', items: fd.completedToday, color: '#22c55e', empty: emptyMessages.done },
            ],
            fd,
        };
    };

    // ── Kanban View ──
    const renderKanban = () => {
        if (!data) return null;
        const { columns } = getColumns();
        const activeCol = columns.find(c => c.key === mobileTab) || columns[0];

        return (
            <>
                {/* Desktop/tablet: horizontal scroll kanban */}
                <div className="wd-kanban">
                    {columns.map(col => (
                        <div key={col.key} className={`wd-kanban-col ${statusFilter && statusFilter !== col.key ? 'wd-kanban-col-dimmed' : ''}`}>
                            <div className="wd-kanban-header" style={{ borderBottomColor: col.color }}>
                                <span>{col.icon} {col.title}</span>
                                <span className="wd-kanban-count">{col.items.length}</span>
                            </div>
                            <div className="wd-kanban-cards">
                                {col.items.length === 0 ? (
                                    <div className="wd-kanban-empty">{col.empty}</div>
                                ) : col.items.map(item => renderCard(item, col.key as any))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Mobile: tabbed view */}
                <div className="wd-mobile-kanban">
                    <div className="wd-mobile-tabs">
                        {columns.map(col => (
                            <button
                                key={col.key}
                                className={`wd-mobile-tab ${mobileTab === col.key ? 'wd-mobile-tab-active' : ''}`}
                                onClick={() => setMobileTab(col.key)}
                                style={mobileTab === col.key ? { borderBottomColor: col.color } : {}}
                            >
                                <span className="wd-mobile-tab-icon">{col.icon}</span>
                                <span className="wd-mobile-tab-label">{col.title}</span>
                                {col.items.length > 0 && <span className="wd-mobile-tab-count">{col.items.length}</span>}
                            </button>
                        ))}
                    </div>
                    <div className="wd-mobile-content">
                        {activeCol.items.length === 0 ? (
                            <div className="wd-kanban-empty">{activeCol.empty}</div>
                        ) : activeCol.items.map(item => renderCard(item, activeCol.key as any))}
                    </div>
                </div>
            </>
        );
    };

    // ── Today's Summary ──
    const todaySummary = () => {
        if (!data) return { active: 0, done: 0, waiting: 0 };
        return {
            active: (data.inProgress?.length || 0),
            done: (data.completedToday?.length || 0),
            waiting: (data.waiting?.length || 0),
            paused: (data.paused?.length || 0),
            blocked: (data.blocked?.length || 0),
        };
    };

    if (loading) {
        return (
            <div className="wd-page">
                <div className="wd-loading">
                    <div className="wd-spinner" />
                    <span>Duke ngarkuar...</span>
                </div>
            </div>
        );
    }

    const summary = todaySummary();
    const noTasks = !data || (summary.active + summary.done + summary.waiting + summary.paused + summary.blocked === 0);

    return (
        <div className="wd-page">
            {/* Navy header — consistent with other modules */}
            <div className="wd-page-header">
                <h1 className="wd-page-title">Paneli im</h1>
            </div>

            {/* Greeting + info row */}
            <div className="wd-greeting-row">
                <div className="wd-greeting-left">
                    <span className="wd-greeting-text">{getGreeting()}, {userName.split(' ')[0] || userName}</span>
                    <span className="wd-greeting-sep">·</span>
                    <span className="wd-greeting-date">{getTodayDate()}</span>
                    <span className="wd-greeting-sep">·</span>
                    <span className="wd-greeting-tasks">{summary.active + summary.waiting + summary.paused + summary.blocked} detyra{summary.active > 0 && <span className="wd-live-dot" />}</span>
                </div>
                <div className="wd-greeting-right">
                    <div className="wd-view-toggle">
                        <button className={`wd-view-btn ${viewMode === 'kanban' ? 'wd-view-active' : ''}`} onClick={() => handleViewChange('kanban')} title="Kanban">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="12" rx="1"/><rect x="17" y="3" width="5" height="8" rx="1"/></svg>
                        </button>
                        <button className={`wd-view-btn ${viewMode === 'list' ? 'wd-view-active' : ''}`} onClick={() => handleViewChange('list')} title="Lista">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Filter pills row */}
            <div className="wd-filters-row">
                <div className="wd-status-pills">
                    <button className={`wd-pill ${statusFilter === 'waiting' ? 'wd-pill-selected' : ''}`} onClick={() => toggleStatusFilter('waiting')}>
                        <span className={`wd-pill-dot ${summary.waiting > 0 ? 'wd-pill-dot-pulse' : ''}`} style={{ background: '#60a5fa' }} />
                        Në pritje <span className="wd-pill-count">{summary.waiting}</span>
                    </button>
                    <button className={`wd-pill ${statusFilter === 'active' ? 'wd-pill-selected' : ''}`} onClick={() => toggleStatusFilter('active')}>
                        <span className={`wd-pill-dot ${summary.active > 0 ? 'wd-pill-dot-pulse' : ''}`} style={{ background: '#fbbf24' }} />
                        Aktive <span className="wd-pill-count">{summary.active}</span>
                    </button>
                    <button className={`wd-pill ${statusFilter === 'paused' ? 'wd-pill-selected' : ''}`} onClick={() => toggleStatusFilter('paused')}>
                        <span className={`wd-pill-dot ${summary.paused > 0 ? 'wd-pill-dot-pulse' : ''}`} style={{ background: '#fb923c' }} />
                        Pauzuara <span className="wd-pill-count">{summary.paused}</span>
                    </button>
                    <button className={`wd-pill ${statusFilter === 'done' ? 'wd-pill-selected' : ''}`} onClick={() => toggleStatusFilter('done')}>
                        <span className={`wd-pill-dot ${summary.done > 0 ? 'wd-pill-dot-pulse' : ''}`} style={{ background: '#4ade80' }} />
                        Sot <span className="wd-pill-count">{summary.done}</span>
                    </button>
                </div>

                {data?.responsibilityNames && Object.keys(data.responsibilityNames).length > 1 && (
                    <>
                        <span className="wd-pill-sep" />
                        {Object.entries(data.responsibilityNames).map(([id, name]: any) => {
                            const rid = Number(id);
                            const count = [...(data.inProgress || []), ...(data.blocked || []), ...(data.waiting || []), ...(data.paused || [])].filter((item: any) => item.responsibilityId === rid).length;
                            return (
                                <button key={id} className={`wd-pill wd-pill-process ${processFilter === rid ? 'wd-pill-selected' : ''}`} onClick={() => toggleProcessFilter(rid)} style={processFilter === rid ? {} : { borderColor: getProcessColor(rid) }}>
                                    {name} <span className="wd-pill-count">{count}</span>
                                </button>
                            );
                        })}
                    </>
                )}

                {(statusFilter || processFilter) && (
                    <button className="wd-pill-clear" onClick={() => { setStatusFilter(null); setProcessFilter(null); }}>✕</button>
                )}
            </div>

            {noTasks ? (
                <div className="wd-empty">
                    <div className="wd-empty-icon">🎉</div>
                    <div className="wd-empty-title">Nuk keni detyra për momentin</div>
                    <div className="wd-empty-sub">Kur të caktohen detyra të reja, do të shfaqen këtu</div>
                </div>
            ) : viewMode === 'kanban' ? renderKanban() : viewMode === 'list' ? (
                <div className="wd-content">
                    {(() => { const fd = getFilteredData(); return (<>
                        {(!statusFilter || statusFilter === 'active') && renderSection('Në proces', '🔄', fd.inProgress, 'active')}
                        {(!statusFilter || statusFilter === 'blocked') && renderSection('Të bllokuara', '⚠', fd.blocked, 'blocked')}
                        {(!statusFilter || statusFilter === 'waiting') && renderSection('Radhë', '⏳', fd.waiting, 'waiting')}
                        {(!statusFilter || statusFilter === 'paused') && renderSection('Të pauzuara', '⏸', fd.paused, 'paused', true)}
                        {(!statusFilter || statusFilter === 'done') && renderSection('Të përfunduara sot', '✅', fd.completedToday, 'done', true, false)}
                    </>); })()}
                </div>
            ) : null}

            {/* Block modal */}
            {blockModal && (
                <div className="wd-block-overlay" onClick={() => setBlockModal(null)}>
                    <div className="wd-block-modal" onClick={e => e.stopPropagation()}>
                        <div className="wd-block-modal-header">
                            <span>⚠ Raporto bllokim</span>
                            <button className="wd-block-modal-close" onClick={() => setBlockModal(null)}>&times;</button>
                        </div>
                        <div className="wd-block-modal-info">
                            {blockModal.material?.materialName || blockModal.item.responsibilityName}
                        </div>
                        <textarea
                            value={blockComment}
                            onChange={e => setBlockComment(e.target.value)}
                            placeholder="Pershkruaj problemin..."
                            rows={3}
                            className="wd-block-modal-textarea"
                            autoFocus
                        />
                        <div className="wd-block-modal-actions">
                            <button className="wd-btn wd-btn-muted" onClick={() => setBlockModal(null)}>Anulo</button>
                            <button
                                className="wd-btn wd-block-modal-submit"
                                disabled={!blockComment.trim() || updatingId !== null}
                                onClick={() => {
                                    const bm = blockModal;
                                    if (bm.material) {
                                        updateMaterialStatus(bm.item, bm.material, STATUS_RETURNED, blockComment.trim());
                                    } else {
                                        updateProcessStatus(bm.item, STATUS_RETURNED, blockComment.trim());
                                    }
                                    setBlockModal(null);
                                }}
                            >
                                Dërgo bllokimin
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, production: state.production, user: state.user };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        productionActions: bindActionCreators(productionActions as any, dispatch),
        actions: bindActionCreators(actions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(WorkerDashboard as any)));
